import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Container, Movement, CirclePoint } from './styles';

import bgGraph from '~/assets/defaults/graph.svg';
import barOpacity from '~/assets/defaults/bars-opacity50.png';
import bar from '~/assets/defaults/bars-opacity100.png';
import circle from '~/assets/defaults/circle-value.svg';

export interface ISerie {
  value: string;
  selected: boolean;
}

interface IGraph {
  series: ISerie[];
  data: string[] | number[];
}

const Graph: React.FC<IGraph> = ({ series: seriesData, data }) => {
  const seriesRef = useRef<HTMLDivElement>(null);
  const [widthPoint, setWidthPoint] = useState(0);
  const [series, setSeries] = useState<ISerie[]>([]);
  const [serieIndex, setSerieIndex] = useState(0);
  const [values, setValues] = useState<string[]>(['0', '0', '0', '0', '0']);

  const formatNumber = useCallback((num) => {
    if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(1)}M`;
    }
    if (num >= 1_000) {
      return `${(num / 1_000).toFixed(1)}K`;
    }
    return num.toString();
  }, []);

  useEffect(() => {
    if (data.length > 0) {
      const lastValueString = data[data.length - 1] as string;
      const lastValue = parseFloat(lastValueString.replaceAll(',', ''));
      const ceil = Math.ceil(lastValue / 100) * 100;
      const ceilParts = ceil / 4;

      setValues([
        formatNumber(0),
        formatNumber(ceilParts),
        formatNumber(ceilParts * 2),
        formatNumber(ceilParts * 3),
        formatNumber(ceilParts * 4),
      ]);
    }
  }, [data, formatNumber]);

  useEffect(() => {
    setSeries(seriesData);
  }, [seriesData]);

  useEffect(() => {
    if (seriesData.length > 0) {
      if (seriesRef.current) {
        if (seriesRef.current.children.length > 0) {
          setWidthPoint(seriesRef.current.children[0].clientWidth);
        }
      }
    }

    if (widthPoint === 0) {
      setWidthPoint(1);
    }
  }, [seriesData, widthPoint]);

  const handleMouseEnter = useCallback((serie, index) => {
    setSeries((oldState) =>
      oldState.map((oldSerie) => {
        if (oldSerie.value === serie.value) {
          return { ...oldSerie, selected: true };
        }
        return { ...oldSerie, selected: false };
      })
    );
    setSerieIndex(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setSeries(seriesData);
    const serieIndexAux = seriesData.findIndex((serie) => serie.selected);
    setSerieIndex(serieIndexAux);
  }, [seriesData]);

  return (
    <Container className="position-relative">
      {series.length > 0 && (
        <>
          <span>{values[0]}</span>
          <span>{values[1]}</span>
          <span>{values[2]}</span>
          <span>{values[3]}</span>
          <span>{values[4]}</span>
          <div className="graph position-relative">
            <img src={bgGraph} alt="Graph" className="w-100 pb-4" />

            <div className="fix">
              <div className="overflow-hidden">
                <img src={barOpacity} alt="Bar" className="bar-opacity" />
              </div>
            </div>

            <Movement width={widthPoint * (serieIndex + 1)}>
              <div className="overflow-hidden">
                <img src={bar} alt="Bar" className="bar" />
              </div>
            </Movement>
            <CirclePoint
              left={serieIndex * widthPoint}
              top={200 - serieIndex * (220 / series.length)}
              width={widthPoint}
              firstPoint={serieIndex === 0}
              lastPoint={serieIndex === series.length - 1}
            >
              <img src={circle} alt="Value" />
              <div className="value-point">
                <span className="d-block">{data[serieIndex]}</span>
                {/* <span className="d-block">60% of your loan repaid</span> */}
              </div>
            </CirclePoint>
          </div>
          <div ref={seriesRef} className="d-flex justify-content-between">
            {series.map((serie, index) => (
              <div className="w-100 position-relative text-center">
                <span
                  className={`month ${
                    serie.selected && 'month-selected'
                  } w-100 text-center`}
                >
                  {serie.value}
                </span>
                <div
                  onMouseEnter={() => handleMouseEnter(serie, index)}
                  onMouseLeave={() => handleMouseLeave()}
                  className="invisible-div"
                />
              </div>
            ))}
          </div>
        </>
      )}
    </Container>
  );
};

export default Graph;
